import { t } from "@lingui/macro";
import {
  ArchiveProjectsBulkDocument,
  type ArchiveProjectsBulkMutation,
  type ArchiveProjectsBulkMutationVariables,
  BulkProjectUpdateDocument,
  type BulkProjectUpdateMutation,
  type BulkProjectUpdateMutationVariables,
  DeleteProjectsBulkDocument,
  type DeleteProjectsBulkMutation,
  type DeleteProjectsBulkMutationVariables,
  InvoiceProjectsByTypeBulkDocument,
  type InvoiceProjectsByTypeBulkMutation,
  type InvoiceProjectsByTypeBulkMutationVariables,
  PinProjectsBulkDocument,
  type PinProjectsBulkMutation,
  type PinProjectsBulkMutationVariables,
  RequestForInvoicingTypeEnum,
  UnarchiveProjectsBulkDocument,
  type UnarchiveProjectsBulkMutation,
  type UnarchiveProjectsBulkMutationVariables,
  UnpinProjectsBulkDocument,
  type UnpinProjectsBulkMutation,
  type UnpinProjectsBulkMutationVariables,
  UserScopeEnum,
} from "@src/__generated__/graphql";
import type {
  BulkAction,
  BulkSubAction,
} from "@src/components/widgets/bulk-actions/types";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { can } from "@src/utils/components/permissions";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { action, computed, makeObservable } from "mobx";
import Router from "next/router";
import type { Store } from "./Store";

type BulkActionIds = string[] | Set<string>;
type ConfirmActionType =
  | "delete"
  | "archive"
  | "unarchive"
  | "createInvoice"
  | "createCreditNote";

type ConfirmDialogData = {
  action: ConfirmActionType;
  ids: string[];
};

const QUICK_FILTER_PINNED = "pinned";
const QUICK_FILTER_FINISHED = "finished";
const QUICK_FILTER_ARCHIVED = "archived";

export class ProjectsListBulkActionsStore {
  confirmDialog = new DisclosureState<ConfirmDialogData>();

  private deleteMutator = new MutationHelper<
    DeleteProjectsBulkMutation,
    DeleteProjectsBulkMutationVariables
  >(DeleteProjectsBulkDocument);

  private pinMutator = new MutationHelper<
    PinProjectsBulkMutation,
    PinProjectsBulkMutationVariables
  >(PinProjectsBulkDocument);

  private unpinMutator = new MutationHelper<
    UnpinProjectsBulkMutation,
    UnpinProjectsBulkMutationVariables
  >(UnpinProjectsBulkDocument);

  private enableExternalAccessMutator = new MutationHelper<
    BulkProjectUpdateMutation,
    BulkProjectUpdateMutationVariables
  >(BulkProjectUpdateDocument);

  private archiveMutator = new MutationHelper<
    ArchiveProjectsBulkMutation,
    ArchiveProjectsBulkMutationVariables
  >(ArchiveProjectsBulkDocument);

  private unarchiveMutator = new MutationHelper<
    UnarchiveProjectsBulkMutation,
    UnarchiveProjectsBulkMutationVariables
  >(UnarchiveProjectsBulkDocument);

  private invoiceMutator = new MutationHelper<
    InvoiceProjectsByTypeBulkMutation,
    InvoiceProjectsByTypeBulkMutationVariables
  >(InvoiceProjectsByTypeBulkDocument);

  constructor(private store: Store) {
    makeObservable(this);
  }

  @computed
  get bulkActions(): BulkAction[] {
    const subActions: BulkSubAction[] = [];
    const quickFilterValue = this.store.quickFilter.value[0];
    const canArchive = can(
      "project_archive_all",
      "project_archive_own",
      "project_archive_team",
    );
    const canUpdate = can(
      "project_update_all",
      "project_update_own",
      "project_update_team",
    );
    const canCreateInvoicing = can(
      "requestForInvoicing_create_all",
      "requestForInvoicing_create_own",
      "requestForInvoicing_create_team",
    );
    const canPin = can(
      "project_pin_all",
      "project_pin_own",
      "project_pin_team",
    );
    const canDelete = can(
      "project_delete_all",
      "project_delete_own",
      "project_delete_team",
    );

    if (canArchive) {
      if (quickFilterValue === QUICK_FILTER_ARCHIVED) {
        subActions.push({
          id: "unarchive",
          label: () => t`Unarchive`,
          callback: (ids) => this.openConfirmDialog("unarchive", ids),
        });
      } else {
        subActions.push({
          id: "archive",
          label: () => t`Archive`,
          callback: (ids) => this.openConfirmDialog("archive", ids),
        });
      }
    }

    if (canUpdate) {
      subActions.push({
        id: "enable-access-for-client",
        label: () => t`Enable access for client`,
        callback: this.enableExternalAccess,
      });
    }

    if (
      quickFilterValue !== QUICK_FILTER_ARCHIVED &&
      quickFilterValue !== QUICK_FILTER_FINISHED &&
      canCreateInvoicing
    ) {
      subActions.push(
        {
          id: "invoice",
          label: () => t`Invoice`,
          callback: (ids) => this.openConfirmDialog("createInvoice", ids),
        },
        {
          id: "credit-note",
          label: () => t`Credit note`,
          callback: (ids) => this.openConfirmDialog("createCreditNote", ids),
        },
      );
    }

    subActions.push(
      {
        id: "star",
        label: () => t`Star`,
        callback: (selectedIds) =>
          this.store.starProjectModalStore.modalState.onOpen({
            ids: this.normalizeIds(selectedIds),
            action: "bulkStar",
          }),
      },
      {
        id: "unstar",
        label: () => t`Unstar`,
        callback: (selectedIds) =>
          this.store.starProjectModalStore.modalState.onOpen({
            ids: this.normalizeIds(selectedIds),
            action: "bulkUnstar",
          }),
      },
    );

    if (quickFilterValue !== QUICK_FILTER_PINNED && canPin) {
      subActions.push(
        {
          id: "pin",
          label: () => t`Pin`,
          callback: this.pinProjects,
        },
        {
          id: "unpin",
          label: () => t`Unpin`,
          callback: this.unpinProjects,
        },
      );
    }

    if (canDelete) {
      subActions.push({
        id: "delete",
        label: () => t`Delete`,
        callback: (ids) => this.openConfirmDialog("delete", ids),
        isDestructive: true,
      });
    }

    if (subActions.length === 0) {
      return [];
    }

    return [
      {
        id: "project-bulk-actions",
        label: () => t`Actions`,
        icon: "dots-vertical",
        subActions,
      },
    ];
  }

  @computed
  get isConfirmDialogLoading(): boolean {
    const action = this.confirmDialog.additionalData?.action;

    switch (action) {
      case "delete":
        return this.deleteMutator.isLoading.value;
      case "archive":
        return this.archiveMutator.isLoading.value;
      case "unarchive":
        return this.unarchiveMutator.isLoading.value;
      case "createInvoice":
      case "createCreditNote":
        return this.invoiceMutator.isLoading.value;
      default:
        return false;
    }
  }

  // biome-ignore lint/correctness/noInvalidUseBeforeDeclaration: Linter is confused about decorator being the same name as parameter
  @action.bound
  openConfirmDialog(action: ConfirmActionType, ids: BulkActionIds): void {
    this.confirmDialog.onOpen({
      action,
      ids: this.normalizeIds(ids),
    });
  }

  @action.bound
  async pinProjects(ids: BulkActionIds): Promise<void> {
    const normalizedIds = this.normalizeIds(ids);

    if (normalizedIds.length === 0) return;

    const [, error] = await this.pinMutator.mutate({
      id: normalizedIds,
    });

    if (error) return;

    this.onBulkActionSuccess();
  }

  @action.bound
  async unpinProjects(ids: BulkActionIds): Promise<void> {
    const normalizedIds = this.normalizeIds(ids);

    if (normalizedIds.length === 0) return;

    const [, error] = await this.unpinMutator.mutate({
      id: normalizedIds,
    });

    if (error) return;

    this.onBulkActionSuccess();
  }

  @action.bound
  async enableExternalAccess(ids: BulkActionIds): Promise<void> {
    const normalizedIds = this.normalizeIds(ids);

    if (normalizedIds.length === 0) return;

    const [, error] = await this.enableExternalAccessMutator.mutate({
      ids: normalizedIds,
      input: { scopes: [UserScopeEnum.Client] },
    });

    if (error) return;

    this.onBulkActionSuccess();
  }

  @action.bound
  async confirm(): Promise<void> {
    const action = this.confirmDialog.additionalData?.action;
    const ids = this.confirmDialog.additionalData?.ids ?? [];

    if (!action || ids.length === 0) return;

    switch (action) {
      case "delete":
        await this.confirmDelete(ids);
        return;
      case "archive":
        await this.confirmArchive(ids);
        return;
      case "unarchive":
        await this.confirmUnarchive(ids);
        return;
      case "createInvoice":
        await this.confirmCreateInvoice(ids);
        return;
      case "createCreditNote":
        await this.confirmCreateCreditNote(ids);
        return;
      default:
        return;
    }
  }

  private normalizeIds(ids: BulkActionIds): string[] {
    return Array.isArray(ids) ? ids : Array.from(ids);
  }

  @action.bound
  private onBulkActionSuccess(): void {
    this.store.setSelectedProjectIds([]);
    this.store.fetchTableData();
  }

  @action.bound
  private async confirmDelete(ids: string[]): Promise<void> {
    const [, error] = await this.deleteMutator.mutate({ id: ids });

    if (error) return;

    this.onBulkActionSuccess();
  }

  @action.bound
  private async confirmArchive(ids: string[]): Promise<void> {
    const [, error] = await this.archiveMutator.mutate({ id: ids });

    if (error) return;

    this.onBulkActionSuccess();
  }

  @action.bound
  private async confirmUnarchive(ids: string[]): Promise<void> {
    const [, error] = await this.unarchiveMutator.mutate({ id: ids });

    if (error) return;

    this.onBulkActionSuccess();
  }

  @action.bound
  private async confirmCreateInvoice(ids: string[]): Promise<void> {
    const [data, error] = await this.invoiceMutator.mutate({
      id: ids,
      invoice_type: RequestForInvoicingTypeEnum.Invoice,
    });

    if (error) return;

    this.store.setSelectedProjectIds([]);

    if (!data.invoiceProjectsBulk?.id) return;
    await Router.push({
      pathname: "/invoices/for-invoicing/edit",
      query: { id: data.invoiceProjectsBulk.id },
    });
  }

  @action.bound
  private async confirmCreateCreditNote(ids: string[]): Promise<void> {
    const [data, error] = await this.invoiceMutator.mutate({
      id: ids,
      invoice_type: RequestForInvoicingTypeEnum.CreditNote,
    });

    if (error) return;

    this.store.setSelectedProjectIds([]);

    if (!data.invoiceProjectsBulk?.id) return;
    await Router.push({
      pathname: "/invoices/for-invoicing/edit",
      query: { id: data.invoiceProjectsBulk.id },
    });
  }
}
